import { ReactElement, useEffect, useMemo, memo } from 'react';
import {
  Stack,
  TextField,
  Typography,
  Box,
  FormControlLabel,
  Switch,
} from '@mui/material';

import { Subscription, CustomAutocomplete } from '~components/index';
import { RoutesActionFilterChip } from '~modules/Routes/styles';
import { SOURCE_IMPORT_TYPES_OPTIONS } from '~constants/sourceImportType';

import { useOrdersFilterMenuGiverContext } from '../../Context';
import { getMinDatePicker } from '../../utils';
import { OrdersFilterMenuGiverInputsContainer } from '../../styles';

const OrdersFilterMenuGiverVariantInPreparation = (): ReactElement => {
  const {
    currentFilters: {
      filters,
      activeFilters,
      autocompletesRefs,
      onChangeFilterInput,
    },
    handleChangeInputDateFilter,
  } = useOrdersFilterMenuGiverContext();

  useEffect(() => {
    if (activeFilters.sourceImports?.length) {
      autocompletesRefs.sourceImports.current?.setValue(
        activeFilters.sourceImports,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters.sourceImports]);

  const minDateFrom = useMemo(() => getMinDatePicker(), []);

  const minExpirationDateTo = useMemo(
    () => filters.expirationDateTimeFrom ?? minDateFrom,
    [filters.expirationDateTimeFrom, minDateFrom],
  );

  return (
    <OrdersFilterMenuGiverInputsContainer>
      <Stack spacing={0.5}>
        <Stack spacing={0.5} direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            &#8288;Fecha de vencimiento
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={filters.includeWithoutExpirationDate ?? false}
                onChange={(event) =>
                  onChangeFilterInput(
                    'includeWithoutExpirationDate',
                    event.target.checked,
                  )
                }
                size="small"
              />
            }
            label="Sin fecha de vencimiento"
            componentsProps={{
              typography: {
                variant: 'caption',
                fontSize: 10,
              },
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Subscription.DataRetentionMin.DatePicker
            label="Desde"
            value={filters.expirationDateTimeFrom}
            onChange={handleChangeInputDateFilter('expirationDateTimeFrom')}
            minDate={minDateFrom}
            renderInput={(props) => <TextField {...props} />}
          />

          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            -
          </Typography>

          <Subscription.DataRetentionMin.DatePicker
            label="Hasta"
            value={filters.expirationDateTimeTo}
            onChange={handleChangeInputDateFilter('expirationDateTimeTo')}
            minDate={minExpirationDateTo}
            renderInput={(props) => <TextField {...props} />}
          />
        </Stack>
      </Stack>

      <CustomAutocomplete
        innerRef={autocompletesRefs.sourceImports}
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        limitTags={1}
        renderTags={(vals, getTagsProps) =>
          vals.map((val, index) => (
            // eslint-disable-next-line react/jsx-key
            <RoutesActionFilterChip
              avatar={
                <Box
                  component="img"
                  src={val.image.url}
                  alt={val.image.alt}
                  sx={{ height: 18, width: 18, objectFit: 'contain' }}
                />
              }
              label={val.label}
              size="small"
              {...getTagsProps({ index })}
            />
          ))
        }
        disableClearable
        options={SOURCE_IMPORT_TYPES_OPTIONS}
        label="Medios de importación"
        name="sourceImports"
        onChange={(_e, vals) => {
          onChangeFilterInput('sourceImports', vals);
        }}
        getOptionLabel={(option) => option.label.toString()}
        renderOption={(props, option) => (
          <li {...props}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                component="img"
                src={option.image.url}
                alt={option.image.alt}
                sx={{ height: 25, width: 25, objectFit: 'contain' }}
              />

              <Typography sx={{ fontSize: 14 }}>{option.label}</Typography>
            </Stack>
          </li>
        )}
      />
    </OrdersFilterMenuGiverInputsContainer>
  );
};

export default memo(OrdersFilterMenuGiverVariantInPreparation);
